import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { Colors, MediaBreakPointUp, MediaBreakPointDown, transition, utils, drawerWidth, } from '@lean-body/components/styles';
import { LocalStorage } from '@lean-body/src/infra';
import { routes } from '@lean-body/src/route';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useXlMediaQuery } from '@lean-body/src/util/media_query_hooks';
import { useMegalosLiveCalendarVisibility, useWindowSize } from '@lean-body/src/util';
import { ScrollIcon } from '../atoms/icons/scroll_icon';
import { ZIndex } from '../constants';
import { useMissionNotificationStore } from '../stores/mission_notification_store';
const SIDE_MENU_BOTTOM_PADDING = 20;
/**
 *
 * ```
 * - Menu
 *   - MenuHeader
 *   - MenuOverlay
 *   - MenuItemList
 *     - MenuItem[]
 * ```
 */
export const Menu = ({ className, isMegalosUser, me, unreadAnnouncementCount, footer }) => {
    const windowSize = useWindowSize();
    const menuRef = React.useRef(null);
    const bottomRef = React.useRef(null);
    const displayedScrollBar = windowSize.height < 620;
    const menuContainerClass = [className, css(styles.menu)].join(' ');
    const [isScrollBottom, setIsScrollBottom] = React.useState(false);
    const hasUnviewedMissions = useMissionNotificationStore((state) => state.hasUnviewedMissions);
    React.useEffect(() => {
        if (!menuRef)
            return;
        const ref = menuRef.current;
        ref.addEventListener('scroll', toggleScroll);
        return () => {
            ref.removeEventListener('scroll', toggleScroll);
        };
    }, [menuRef]);
    const toggleScroll = () => {
        setIsScrollBottom(menuRef.current.scrollHeight - menuRef.current.clientHeight < menuRef.current.scrollTop + SIDE_MENU_BOTTOM_PADDING);
    };
    const onClickScrollIcon = () => {
        bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: css(styles.container) },
            React.createElement("div", { className: menuContainerClass, ref: menuRef },
                React.createElement(MenuHeader, null),
                React.createElement(MenuItemList, { isMegalosUser: isMegalosUser, unreadAnnouncementCount: unreadAnnouncementCount, me: me, footer: footer, hasUnviewedMissions: hasUnviewedMissions }),
                React.createElement("div", { ref: bottomRef }))),
        displayedScrollBar && !isScrollBottom && (React.createElement("div", { className: css(styles.scrollIconContainer) },
            React.createElement(ScrollIcon, { classObject: styles.scrollIcon, onClick: onClickScrollIcon })))));
};
const styles = StyleSheet.create({
    menu: {
        backgroundColor: Colors.White,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'fixed',
        minHeight: 400,
        borderRight: `1px solid ${Colors.BorderLightGray}`,
        overflow: 'auto',
        '-ms-overflow-style': 'none',
        scrollbarWidth: 'none',
        '::-webkit-scrollbar': {
            display: 'none',
        },
    },
    container: {
        position: 'relative',
    },
    scrollIconContainer: {
        position: 'fixed',
        left: `calc((${drawerWidth.md}px - 40px) / 2)`,
        bottom: 16,
        zIndex: ZIndex.MainMenuScrollIcon,
        [MediaBreakPointUp.XL]: {
            left: `calc((${drawerWidth.xl}px - 40px) / 2)`,
        },
    },
    scrollIcon: {
        cursor: 'pointer',
    },
});
/*=============================================
= Menu Header =
=============================================*/
const MenuHeader = (props) => {
    const styles = StyleSheet.create({
        container: {
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            [MediaBreakPointUp.MD]: {
                padding: '35px 27px',
            },
            [MediaBreakPointUp.XL]: {
                height: 'auto',
                paddingTop: '2rem',
                paddingBottom: '1.5rem',
                marginBottom: 0,
            },
        },
        logo: {
            [MediaBreakPointUp.MD]: {
                width: 17,
                height: 30,
            },
            [MediaBreakPointUp.XL]: {
                maxWidth: 170,
                width: '100%',
                height: '100%',
            },
        },
        link: {
            display: 'block',
            width: '100%',
            textAlign: 'center',
        },
    });
    const containerClass = [props.className || '', css(styles.container)].join(' ');
    const logoClass = css(utils.imgFluid, styles.logo, styles.logo);
    const isXL = useXlMediaQuery();
    const logoSrc = isXL ? '/assets/images/logo-black.svg' : '/assets/images/logo.svg';
    return (React.createElement("div", { className: containerClass },
        React.createElement(Link, { to: "/", className: css(styles.link) },
            React.createElement("img", { src: logoSrc, className: logoClass }))));
};
/*=============================================
= MenuItemList =
=============================================*/
const MenuItemList = observer((props) => {
    const styles = StyleSheet.create({
        menuItemList: {
            height: 'calc(100% - 60px)',
            display: 'flex',
            alignItems: 'flex-start',
            alignContent: 'space-between',
            flexWrap: 'wrap',
            position: 'relative',
            marginBottom: 25,
        },
        menuSection: {
            width: '100%',
        },
    });
    const { footer } = props;
    const isMegalosLiveCalendarVisible = useMegalosLiveCalendarVisibility();
    const localStorage = new LocalStorage();
    const location = useLocation();
    const containerClass = [props.className || '', css(styles.menuItemList)].join(' ');
    const unreadAnnouncementCount = props.unreadAnnouncementCount && props.unreadAnnouncementCount.count;
    const isTopPage = routes.TOP.matchPath(location.pathname);
    const isLiveLessonPage = routes.LIVE_GROUP.matchPath(location.pathname);
    const isMyLessonPage = routes.MY_LESSON.matchPath(location.pathname);
    const isSearchPage = routes.SEARCH_GROUP.matchPath(location.pathname);
    const isActivityPage = routes.ACTIVITY_GROUP.matchPath(location.pathname);
    const isMegalosLiveCalendarPage = routes.MEGALOS_LIVE_CALENDAR.matchPath(location.pathname);
    const isAnnouncementPage = routes.ANNOUNCEMENT_GROUP.matchPath(location.pathname);
    const isSettingPage = routes.SETTING.matchPath(location.pathname);
    const hasLiveLessonNotification = !isLiveLessonPage && !!(footer === null || footer === void 0 ? void 0 : footer.hasLiveLessonNotification);
    if (isLiveLessonPage && (footer === null || footer === void 0 ? void 0 : footer.hasLiveLessonNotification)) {
        localStorage.saveLiveScheduleTabNoticed();
    }
    return (React.createElement("div", { className: containerClass },
        React.createElement("div", { className: css(styles.menuSection) },
            React.createElement(MenuItem, { to: "/", text: "\u30DB\u30FC\u30E0", imgSrc: "/assets/images/icons/home.svg", imgSrcActive: "/assets/images/icons/home_light_blue.svg", isActive: isTopPage }),
            React.createElement(MenuItem, { to: routes.SEARCH.compile(), text: "\u898B\u3064\u3051\u308B", imgSrc: "/assets/images/icons/search.svg", imgSrcActive: "/assets/images/icons/search_light_blue.svg", isActive: isSearchPage }),
            React.createElement(MenuItem, { to: "/live_schedule", text: "LEAN BODY LIVE", imgSrc: "/assets/images/icons/live_stream.svg", imgSrcActive: "/assets/images/icons/live_stream_light_blue.svg", hasNotification: hasLiveLessonNotification, isActive: isLiveLessonPage }),
            React.createElement(MenuItem, { to: routes.MY_LESSON.compile(), text: "MY\u30EC\u30C3\u30B9\u30F3", imgSrc: "/assets/images/icons/my_lesson.svg", imgSrcActive: "/assets/images/icons/my_lesson_light_blue.svg", isActive: isMyLessonPage }),
            React.createElement(MenuItem, { to: routes.ACTIVITY.compile(), text: "\u30A2\u30AF\u30C6\u30A3\u30D3\u30C6\u30A3", imgSrc: "/assets/images/icons/activity.svg", imgSrcActive: "/assets/images/icons/activity_light_blue.svg", hasNotification: props.hasUnviewedMissions, isActive: isActivityPage }),
            props.isMegalosUser && isMegalosLiveCalendarVisible && (React.createElement(MenuItem, { to: "/megalos_live_calendar", text: "\u30E1\u30AC\u30ED\u30B9 LIVE\u30EC\u30C3\u30B9\u30F3", imgSrc: "/assets/images/icons/megalos_live.svg", imgSrcActive: "/assets/images/icons/megalos_live_light_blue.svg", isActive: isMegalosLiveCalendarPage }))),
        React.createElement("div", { className: css(styles.menuSection) },
            React.createElement(MenuItem, { to: "/announcements", text: "\u304A\u77E5\u3089\u305B", imgSrc: "/assets/images/icons/bell.svg", imgSrcActive: "/assets/images/icons/bell_light_blue.svg", badgeCount: unreadAnnouncementCount, isActive: isAnnouncementPage }),
            React.createElement(MenuItem, { to: routes.SETTING.compile(), text: "\u8A2D\u5B9A", imgSrc: "/assets/images/icons/setting.svg", imgSrcActive: "/assets/images/icons/setting_light_blue.svg", isActive: isSettingPage }))));
});
const MenuItem = (props) => {
    const styles = StyleSheet.create({
        menuItem: {
            height: 50,
            width: '100%',
            position: 'relative',
            marginBottom: 10,
            [MediaBreakPointUp.MD]: {
                padding: 0,
                marginRight: 0,
            },
            [MediaBreakPointUp.XL]: {
                padding: '0 20px',
                marginRight: 6,
            },
        },
        link: {
            display: 'flex',
            alignItems: 'center',
            fontSize: 14,
            borderRadius: 25,
            lineHeight: 1,
            textDecoration: 'none',
            cursor: 'pointer',
            transition: transition(),
            ':hover': {
                backgroundColor: Colors.BackgroundLightGray,
            },
            [MediaBreakPointUp.MD]: {
                width: 43,
                height: 43,
                padding: 6,
                margin: '3px 14px',
            },
            [MediaBreakPointUp.XL]: {
                width: '100%',
                height: '100%',
                padding: '10px 0 10px 20px',
                margin: 'auto',
            },
        },
        text: {
            fontSize: 14,
            fontWeight: 500,
            color: Colors.Black,
            letterSpacing: '0.04em',
            transition: transition('color'),
        },
        activeText: {
            color: Colors.PrimaryDarkB,
        },
        badge: {
            fontSize: 12,
            fontWeight: 'bold',
            textAlign: 'center',
            width: 16,
            height: 16,
            backgroundColor: Colors.Accent,
            color: Colors.White,
            borderRadius: 100,
            margin: '-1px 0 0 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            [MediaBreakPointDown.XL]: {
                fontSize: 7.5,
                width: 15.25,
                height: 15.25,
                top: 5,
                right: 23,
                paddingBottom: 2,
                position: 'absolute',
            },
        },
        iconContainer: {
            position: 'relative',
            width: 28,
            height: 28,
            flexShrink: 0,
            marginLeft: 1,
            [MediaBreakPointUp.MD]: {
                marginRight: 0,
            },
            [MediaBreakPointUp.XL]: {
                marginRight: 12,
            },
        },
        imgSrcIcon: {
            width: 28,
            height: 28,
            transition: transition('color'),
        },
        notification: {
            backgroundColor: Colors.Accent,
            width: 5.25,
            height: 5.25,
            position: 'absolute',
            top: 0,
            right: 0,
            borderRadius: 100,
        },
        message: {
            fontSize: 12,
            fontWeight: 'bold',
            color: Colors.White,
            backgroundColor: Colors.Accent,
            padding: '2px 4px',
            borderRadius: 100,
            marginLeft: 14,
            [MediaBreakPointDown.XL]: {
                fontSize: 7.5,
                padding: '2px 3px',
                position: 'absolute',
                top: 7,
                right: 11,
            },
        },
    });
    const { dataTest, to, onClick, imgSrc, imgSrcActive, text, badgeCount, hasNotification, isActive, message } = props;
    const isXL = useXlMediaQuery();
    return (React.createElement("div", { className: css(styles.menuItem), "data-test": dataTest }, to ? (React.createElement(Link, { to: to, className: css(styles.link) },
        imgSrc && (React.createElement("div", { className: css(styles.iconContainer) },
            React.createElement("img", { src: isActive ? imgSrcActive : imgSrc, className: css(styles.imgSrcIcon) }),
            hasNotification && React.createElement("div", { className: css(styles.notification) }))),
        isXL && React.createElement("div", { className: css(styles.text, isActive && styles.activeText) }, text),
        badgeCount ? React.createElement("div", { className: css(styles.badge) }, badgeCount) : '',
        !!message && React.createElement("div", { className: css(styles.message) }, message))) : (React.createElement("div", { className: css(styles.link), onClick: onClick },
        imgSrc && (React.createElement("div", { className: css(styles.iconContainer) },
            React.createElement("img", { src: isActive ? imgSrcActive : imgSrc, className: css(styles.imgSrcIcon) }),
            hasNotification && React.createElement("div", { className: css(styles.notification) }))),
        isXL && React.createElement("div", { className: css(styles.text, isActive && styles.activeText) }, text),
        badgeCount ? React.createElement("div", { className: css(styles.badge) }, badgeCount) : '',
        !!message && React.createElement("div", { className: css(styles.message) }, message)))));
};
